import React from "react"
import PropTypes from "prop-types"
import { Grid, TextField, IconButton } from "@mui/material"
import DeleteOutline from "@mui/icons-material/DeleteOutline"
import i18n from "i18n"

import TaxField from "components/ProfileRefinament/Taxes/TaxField"
import InvoiceNumericTextField from "./InvoiceNumericTextField"
import {
  getConceptTotal,
  isGroupedCertificationConcept,
  INVOICE_TEXT_MAX_LENGTH,
} from "../utils/invoiceUtils"

const InvoiceConceptRow = ({
  concept,
  idx,
  onChange,
  onTaxesChange,
  onDelete,
  disableDelete,
  taxOptions,
  hasValidationErrors,
  showProductDiscount = false,
  issued = false,
  readOnly = false,
}) => {
  const total = getConceptTotal(
    concept.quantity || 0,
    concept.price || 0,
    concept.taxes || [],
    concept.discount || 0,
  )

  const descriptionWidth = showProductDiscount ? 4.5 : 5
  const totalWidth = showProductDiscount ? 1 : 1.5

  const isLocked = issued || readOnly
  const isTaxLocked = issued
  const isDescriptionReadOnly =
    issued ||
    !!concept.partidaId ||
    (readOnly && !isGroupedCertificationConcept(concept))
  const descriptionReadOnlyStyle = isDescriptionReadOnly
    ? { pointerEvents: "none", background: "#f5f5f5" }
    : {}

  const issuedReadOnlyStyle = isLocked
    ? { pointerEvents: "none", background: "#f5f5f5" }
    : {}

  const quantityMax =
    concept.partidaId && typeof concept.maxQuantity === "number"
      ? concept.maxQuantity
      : undefined

  return (
    <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
      <Grid item xs={descriptionWidth}>
        <TextField
          fullWidth
          size="small"
          label={i18n.t("invoices.description")}
          required
          value={concept.description}
          onChange={e => onChange(idx, "description", e.target.value)}
          error={
            hasValidationErrors &&
            (!concept.description || concept.description.trim() === "")
          }
          inputProps={{
            maxLength: INVOICE_TEXT_MAX_LENGTH,
            readOnly: isDescriptionReadOnly,
            style: descriptionReadOnlyStyle,
          }}
        />
      </Grid>
      <Grid item xs={1}>
        <InvoiceNumericTextField
          fullWidth
          size="small"
          label={i18n.t("invoices.quantity")}
          value={concept.quantity}
          onCommit={n => onChange(idx, "quantity", n)}
          emptyFallback={1}
          min={1}
          max={quantityMax}
          readOnly={isLocked}
          inputProps={{
            min: 1,
            ...(quantityMax !== undefined ? { max: quantityMax } : {}),
            readOnly: isLocked,
            style: issuedReadOnlyStyle,
          }}
        />
      </Grid>
      <Grid item xs={1}>
        <InvoiceNumericTextField
          fullWidth
          size="small"
          label={i18n.t("invoices.unitPrice")}
          value={concept.price}
          onCommit={n => onChange(idx, "price", n)}
          emptyFallback={0}
          min={0}
          readOnly={isLocked}
          inputProps={{
            min: 0,
            step: 0.01,
            readOnly: isLocked,
            style: issuedReadOnlyStyle,
          }}
        />
      </Grid>
      {showProductDiscount && (
        <Grid item xs={1}>
          <InvoiceNumericTextField
            fullWidth
            size="small"
            label={i18n.t("invoices.discountPercent")}
            value={concept.discount}
            onCommit={n => onChange(idx, "discount", n)}
            emptyFallback={0}
            min={0}
            max={100}
            readOnly={isLocked}
            inputProps={{
              min: 0,
              max: 100,
              step: 0.01,
              readOnly: isLocked,
              style: issuedReadOnlyStyle,
            }}
          />
        </Grid>
      )}
      <Grid item xs={3}>
        <TaxField
          size="small"
          fullWidth
          options={taxOptions}
          label={i18n.t("invoices.taxes")}
          selectedTaxes={concept.taxes}
          onChange={taxes => onTaxesChange(idx, taxes)}
          readOnly={isTaxLocked}
        />
      </Grid>
      <Grid item xs={totalWidth}>
        <TextField
          fullWidth
          size="small"
          label={i18n.t("invoices.total")}
          value={total.toLocaleString("es-ES", {
            style: "currency",
            currency: "EUR",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
          inputProps={{
            readOnly: true,
            style: {
              textAlign: "right",
              ...(isLocked
                ? { pointerEvents: "none", background: "#f5f5f5" }
                : {}),
            },
          }}
        />
      </Grid>
      <Grid item xs={0.5}>
        {!isLocked && (
          <IconButton onClick={() => onDelete(idx)} disabled={disableDelete}>
            <DeleteOutline color={disableDelete ? "disabled" : "error"} />
          </IconButton>
        )}
      </Grid>
    </Grid>
  )
}

InvoiceConceptRow.propTypes = {
  concept: PropTypes.shape({
    description: PropTypes.string,
    quantity: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
    price: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
    taxes: PropTypes.array,
    retentions: PropTypes.array,
    partidaId: PropTypes.string,
    maxQuantity: PropTypes.number,
    discount: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
    certsAssociated: PropTypes.array,
  }).isRequired,
  idx: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  onTaxesChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  disableDelete: PropTypes.bool.isRequired,
  taxOptions: PropTypes.array.isRequired,
  hasValidationErrors: PropTypes.bool,
  showProductDiscount: PropTypes.bool,
  issued: PropTypes.bool,
  readOnly: PropTypes.bool,
}

export default InvoiceConceptRow
